// TODO FIXME: Create constants for date formatters, which is known to be slow otherwise

export type DateMode = 'FULL' | 'DATE_ONLY' | 'TIME_ONLY';

export type Locale = 'en-us' | 'de';

export type DateFormat =
	| 'MONT_SHORT'
	| 'MONTH_FULL'
	| 'DAY_SHORT, DD/MM/YYYY'
	| 'DAY_FULL, DD/MM'
	| 'DD/MM/YYYY HH:mm'
	| 'DD/MM/YYYY'
	| 'HH:mm'
	| 'DD'
	| 'DD MONTH_SHORT'
	| 'MONTH_SHORT DD/MM/YYY HH:mm'
	| 'MONTH_FULL DD/MM/YYY';

// Initialized once as repeatedly creating them can be expensive
// TOOD: Initialize lazily (i.e. create live and cache)
const formatters: Record<Locale, Record<DateFormat, Intl.DateTimeFormat>> = {
	de: createFormatters('de'),
	'en-us': createFormatters('en-us')
};

function createFormatters(locale: Locale): Record<DateFormat, Intl.DateTimeFormat> {
	return {
		MONT_SHORT: new Intl.DateTimeFormat(locale, {
			month: 'short'
		}),
		MONTH_FULL: new Intl.DateTimeFormat(locale, {
			month: 'long'
		}),
		'DAY_SHORT, DD/MM/YYYY': new Intl.DateTimeFormat(locale, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			weekday: 'short'
		}),
		'DAY_FULL, DD/MM': new Intl.DateTimeFormat(locale, {
			day: '2-digit',
			month: '2-digit',
			weekday: 'long'
		}),
		'DD/MM/YYYY': new Intl.DateTimeFormat(locale, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		}),
		'DD MONTH_SHORT': new Intl.DateTimeFormat(locale, {
			day: '2-digit',
			month: 'long'
		}),
		'HH:mm': new Intl.DateTimeFormat(locale, {
			hour: '2-digit',
			minute: '2-digit',
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		}),
		'DD/MM/YYYY HH:mm': new Intl.DateTimeFormat(locale, {
			hour: '2-digit',
			minute: '2-digit',
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		}),
		DD: new Intl.DateTimeFormat(locale, {
			day: '2-digit'
		}),
		'MONTH_SHORT DD/MM/YYY HH:mm': new Intl.DateTimeFormat(locale, {
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			year: 'numeric',
			minute: '2-digit',
			hour12: false
		}),
		'MONTH_FULL DD/MM/YYY': new Intl.DateTimeFormat(locale, {
			month: 'long',
			day: '2-digit',
			year: 'numeric',
			hour12: false
		})
	};
}

export function parseISODate(isoDate: string): Date {
	return new Date(isoDate);
}

export function getShortMonthName(date: Date, locale: Locale = 'de'): string {
	return formatters[locale].MONT_SHORT.format(date);
}

export function getFullMonthName(date: Date, locale: Locale = 'de'): string {
	return date.toLocaleString(locale, { month: 'long' });
}

export function getDayName(date: Date): string {
	return getDayNameForDayIndex(date.getDay()) || '';
}

export function getDayNameForDayIndex(dayIndex: number): string | undefined {
	return {
		0: 'Sonntag',
		1: 'Montag',
		2: 'Dienstag',
		3: 'Mittwoch',
		4: 'Donnerstag',
		5: 'Freitag',
		6: 'Samstag'
	}[dayIndex];
}

export function get2DigitDay(date: Date, locale: Locale = 'de'): string {
	return formatters[locale]['DD'].format(date);
}

export function formatDateLegacy(date: Date): string {
	return date.toLocaleDateString('de');
}

export function formatDate(date: Date, locale: Locale, format: DateFormat) {
	return formatters[locale][format].format(date);
}

export function isOnSameDay(date1: Date, date2: Date) {
	return formatDate(date1, 'en-us', 'DD/MM/YYYY') === formatDate(date2, 'en-us', 'DD/MM/YYYY');
}

export function formatDateWithoutTime(date: Date, format: Locale = 'de'): string {
	return formatters[format]['DD/MM/YYYY'].format(date);
}

export function getWeekNumber(date: Date) {
	date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
	const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
	const weekNumber = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
	return weekNumber;
}

export function formatDateWithWeekdayAndWithoutTime(
	date: Date,
	niceFormat = false,
	locale: Locale = 'de'
): string {
	if (niceFormat) {
		const humanReadable = getHumanReadableFormat(date, locale);
		if (humanReadable) {
			return humanReadable;
		}
	}
	return formatters[locale]['DAY_SHORT, DD/MM/YYYY'].format(date);
}

export function formatDateWithoutTimeAndYear(
	date: Date,
	niceFormat = false,
	locale: Locale = 'de'
): string {
	if (niceFormat) {
		const humanReadable = getHumanReadableFormat(date, locale);
		if (humanReadable) {
			return humanReadable;
		}
	}
	return formatters[locale]['DD MONTH_SHORT'].format(date);
}

export function getHumanReadableFormat(date: Date, lang: Locale = 'en-us'): string | null {
	const now = new Date();
	const dateFormatted = formatDateWithoutTime(date);
	if (dateFormatted === formatDateWithoutTime(now)) {
		return lang == 'en-us' ? 'today' : 'Heute';
	}
	const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
	if (dateFormatted === formatDateWithoutTime(tomorrow)) {
		return lang == 'en-us' ? 'tomorrow' : 'Morgen';
	}
	const dayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
	if (dateFormatted === formatDateWithoutTime(dayAfterTomorrow)) {
		return lang == 'en-us' ? 'in 2 days' : 'Übermorgen';
	}
	const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	if (dateFormatted === formatDateWithoutTime(yesterday)) {
		return lang == 'en-us' ? 'yesterday' : 'Gestern';
	}
	return null;
}

export function formatDateWithTime(date: Date, locale: Locale = 'de', niceFormat = false): string {
	if (niceFormat) {
		const humanReadable = getHumanReadableFormat(date, locale);
		if (humanReadable) {
			return (
				humanReadable +
				' ' +
				date.toLocaleTimeString([locale], {
					hour: '2-digit',
					minute: '2-digit',
					hour12: false
				})
			);
		}
	}
	return formatters[locale]['MONTH_SHORT DD/MM/YYY HH:mm'].format(date);
}

/** Returns the date as YYYY-MM-DD, which is required for HTML input elements with type 'date' */
export function formatForDateInput(date: Date): string {
	return date.toLocaleDateString('en-CA');
}

export function formatDateWithTimeOnly(date: Date, locale: Locale = 'de'): string {
	return formatters[locale]['HH:mm'].format(date);
}

export function getNowUTC() {
	const now = new Date();
	return Date.UTC(
		now.getUTCFullYear(),
		now.getUTCMonth(),
		now.getUTCDate(),
		now.getUTCHours(),
		now.getUTCMinutes(),
		now.getUTCSeconds(),
		now.getUTCMilliseconds()
	);
}

export function dateToUTCDate(input: Date) {
	return new Date(
		input.getUTCFullYear(),
		input.getUTCMonth(),
		input.getUTCDate(),
		input.getUTCHours(),
		input.getUTCMinutes(),
		input.getUTCSeconds()
	);
}
